import React, { useCallback } from 'react';

import { bool, string } from 'prop-types';
import { useDispatch } from 'react-redux';
import { TextField } from '@picsio/ui';

import { setAltText } from '../../../../store/actions/assets';
import Logger from '../../../../services/Logger';
import localization from '../../../../shared/strings';
import {
  useBlurCommit,
  useEnterToBlur,
  useLocalFieldValue,
} from '../../../ProductInfo/hooks';

const ALT_MAX_LENGTH = 2048;

const trimAltTextValue = (value) => String(value ?? '').trim();

function AltTextDefaultField({ assetId, value, disabled }) {
  const dispatch = useDispatch();
  const strings = localization.ALT_TEXT;
  const { localValue, setLocalValue } = useLocalFieldValue(value ?? '');
  const handleEnter = useEnterToBlur();

  const saveValue = useCallback((nextValue) => {
    dispatch(setAltText(assetId, nextValue ?? ''));
    Logger.log('User', 'ProductsDefaultAltTextSaved', {
      assetId,
      isClearing: nextValue === '',
    });
  }, [assetId, dispatch]);

  const { handleBlur: commitOnBlur } = useBlurCommit({
    value: value ?? '',
    localValue,
    setLocalValue,
    onCommit: (nextValue) => {
      const trimmedNext = trimAltTextValue(nextValue);
      const trimmedCurrent = trimAltTextValue(value);
      if (trimmedNext === trimmedCurrent) return;
      saveValue(trimmedNext);
    },
    serialize: trimAltTextValue,
  });

  const handleBlur = useCallback(() => {
    const trimmed = trimAltTextValue(localValue);
    if (trimmed !== localValue) {
      setLocalValue(trimmed);
    }
    commitOnBlur();
  }, [commitOnBlur, localValue, setLocalValue]);

  return (
    <TextField
      label={strings.defaultAltLabel}
      value={localValue}
      placeholder={strings.placeholderEditable}
      disabled={disabled}
      inputProps={{
        maxLength: ALT_MAX_LENGTH,
        onKeyDown: handleEnter,
      }}
      onChange={(event) => {
        if (disabled) return;
        setLocalValue(event.target.value);
      }}
      onBlur={handleBlur}
    />
  );
}

AltTextDefaultField.propTypes = {
  assetId: string.isRequired,
  value: string,
  disabled: bool,
};

AltTextDefaultField.defaultProps = {
  value: '',
  disabled: false,
};

export default AltTextDefaultField;
